if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        pagination: this.querySelector('[data-swiper-pagination]'),
        swiperElement: this.querySelector('[data-swiper]'),
        activeSlide: false,
        swiperInstance: false,
        zoomToggleButton: this.querySelector('[data-media-zoom-toggle]') || false,
        pauseButton: this.querySelector('[data-swiper-pause]') || false
      };

      this.handleMediaGalleryToggle = this.handleMediaGalleryToggle.bind(this);
      this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
      this.handleSwiperResize = this.handleSwiperResize.bind(this);
      this.handlePauseClick = this.handlePauseClick.bind(this);
    }

    handleVariantUpdate(event) {
      // update active slide
      if (!this.hasAttribute('data-update-variant-images')) {
        const featuredMediaId = parseInt(event?.data?.variant?.featured_media?.id, 10);

        if (Number.isNaN(featuredMediaId)) {
          return;
        }

        const swiperSlides = this.elements.swiperElement.querySelectorAll('[data-swiper-slide]');
        const matchedIndex = Array.from(swiperSlides).findIndex(slide => slide.getAttribute('data-slide-id') == featuredMediaId);

        if (!Number.isNaN(matchedIndex) && matchedIndex > -1) {
          this.elements.swiperInstance?.slideTo(matchedIndex);
        }

        return;
      }

      // rebuild new swiper instance
      this.elements.swiperInstance.destroy()
      const newSwiper = event?.data?.html?.querySelector('[data-swiper]');

      if (this.elements.swiperElement && newSwiper) {
        this.elements.swiperElement.replaceWith(newSwiper);

        requestAnimationFrame(() => {
          this.init();
        });
      }
    }

    connectedCallback() {
      this.init();

      window.addEventListener('resize', this.handleSwiperResize);

      this.variantIdChangedUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantUpdate
      );

      this.mediaGalleryStateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.toggleMediaGallery, 
        this.handleMediaGalleryToggle
      );
    }

    handleSwiperResize() {
      this.elements.swiperInstance?.update();
    }

    swiperInit() {
      this.elements.swiperElement = this.querySelector('[data-swiper]');

      if (!this.elements.swiperElement) { return; }

      const prevButton = this.elements.swiperElement.querySelector('[data-swiper-prev]');
      const nextButton = this.elements.swiperElement.querySelector('[data-swiper-next]');
      const pagination = this.elements.swiperElement.querySelector('[data-swiper-pagination]');
      const loop = this.elements.swiperElement.hasAttribute('data-swiper-loop');
      const initialSlide = this.elements.swiperElement.hasAttribute('data-initial-slide-index') ? parseInt(this.elements.swiperElement.getAttribute('data-initial-slide-index'), 10) : 0;

      this.elements.pagination = pagination
      const paginationData = pagination ? {
        el: pagination,
        clickable: true
      } : false;

      const navigationData = prevButton && nextButton ? {
        prevEl: prevButton,
        nextEl: nextButton
      } : false;

      const swiper = new window.Swiper(this.elements.swiperElement, {
        spaceBetween: 0,
        slidesPerView: 1,
        autoHeight: true,
        draggable: true,
        loop: loop,
        threshold: 20,
        initialSlide,
        navigation: navigationData,
        pagination: paginationData,
        autoplay: {
          delay: 6000,
          disableOnInteraction: false
        }
      });

      swiper.on('slideChangeTransitionStart', () => {
        this.setActiveMedia();
        this.updatePagination()
        this.updateZoomToggleButton();
      });

      swiper.on('slideChangeTransitionEnd', () => {
        this.playActiveMedia();
        this.updateAvatar();
      });

      return swiper;
    }

    init() {
      this.elements.swiperInstance = this.swiperInit();
      this.setActiveMedia();
      this.updatePagination()
      this.updateAvatar();
      this.createPauseEventListener();
      this.elements.zoomToggleButton = this.querySelector('[data-media-zoom-toggle]') || false
    }

    createPauseEventListener() {
      this.elements.pauseButton = this.querySelector('[data-swiper-pause]') || false

      if (this.elements.pauseButton) {
        this.elements.pauseButton.addEventListener('click', this.handlePauseClick);
      }
    }

    updatePagination() {
      if (!this.elements.pagination || !this.elements.swiperInstance) { return; }
        
      const paginationItems = this.elements.pagination.querySelectorAll('.swiper-pagination-bullet');

      if (paginationItems && paginationItems.length) {
        paginationItems.forEach((paginationItem, index) => {
          const { realIndex } = this.elements.swiperInstance;

          if (realIndex + 1 > paginationItems.length) {
            paginationItem.classList.remove('swiper-pagination-bullet--filled');
            return;
          }

          if (index < realIndex) {
            paginationItem.classList.add('swiper-pagination-bullet--filled');
            return
          }

          paginationItem.classList.remove('swiper-pagination-bullet--filled');
        });
      }
    }
    
    updateAvatar() {
      const activeSlide = this.elements.activeSlide;
      const avatarWrapper = this.elements.swiperElement.querySelector('[data-swiper-avatar-wrapper]');

      if (!avatarWrapper) { return; }
    
      if (activeSlide) {
        const slideImage = activeSlide.querySelector('[data-product-media-image]');
        const avatar = avatarWrapper.querySelector('[data-swiper-avatar]');
        const avatarText = avatarWrapper.querySelector('[data-swiper-avatar-text]');

        if (avatarWrapper && slideImage && slideImage.hasAttribute('data-variant-title')) {
          avatarWrapper.classList.remove('d-none');
          avatar.setAttribute('src', slideImage.getAttribute('src'));
          avatarText.innerHTML = slideImage.getAttribute('data-variant-title') ? slideImage.getAttribute('data-variant-title') : '';
          return;
        }
      }

      avatarWrapper.classList.add('d-none');
    }

    updateZoomToggleButton() {
      const activeSlide = this.elements.activeSlide;
 
      if (!activeSlide) { return; }
      const deferredElement = activeSlide.querySelector('deferred-media');
      const productModel = activeSlide.querySelector('product-model');

      if (this.elements.zoomToggleButton) {
        this.elements.zoomToggleButton.setAttribute('data-media-id', activeSlide.getAttribute('data-slide-id'));
      }

      if (productModel || deferredElement) {
        this.elements.swiperElement.classList.add('swiper-deferred-media');
        return;
      }

      this.elements.swiperElement.classList.remove('swiper-deferred-media');
    }

    handleMediaGalleryToggle(event) {
      const { data: { pause } } = event;
      const { swiperElement, swiperInstance } = this.elements;
      const pauseButton = swiperElement?.querySelector('[data-swiper-pause]');
  
      if (!pauseButton || !swiperInstance) return;
  
      this.updateSwiperState(swiperElement, swiperInstance, pauseButton, pause);
    }
  
    updateSwiperState(swiperElement, swiperInstance, pauseButton, isPaused) {
      swiperElement.classList.toggle('swiper-paused', isPaused);
      swiperElement.classList.toggle('swiper-zoomed', isPaused);
      pauseButton.classList.toggle('active', isPaused);
  
      swiperInstance.allowTouchMove = !isPaused;
      
      if (isPaused) {
        swiperInstance.autoplay.stop();
      } else {
        swiperInstance.autoplay.start();
      }
    }

    stopVideos() {
      window.pauseAllMedia()
    }

    setActiveMedia() {
      this.elements.activeSlide = this.elements.swiperElement.querySelector('.swiper-slide-active');

      if (this.elements.activeSlide) {
        const slidePosition = this.elements.activeSlide.getAttribute('data-swiper-slide-index');
        const slideImage = this.elements.activeSlide.querySelector('[data-product-media-image]');

        if (slideImage) {
          this.announceLiveRegion(slideImage, slidePosition);
        }
      }
    }

    playActiveMedia() {
      this.stopVideos();
      const activeSlide = this.elements.activeSlide;

      if (!activeSlide) { return; }
      const deferredMedia = activeSlide.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    announceLiveRegion(image, position) {
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);

        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };

      image.src = image.src;
    }

    handlePauseClick() {
      const { swiperElement, swiperInstance, pauseButton } = this.elements;
      
      swiperElement.classList.toggle('swiper-paused');
      pauseButton.classList.toggle('active');

      if (pauseButton.classList.contains('active')) {
        swiperInstance.autoplay.stop();
      } else {
        swiperInstance.autoplay.start();
      }
    }

    disconnectedCallback() {
      if (this.elements.swiperInstance) {
        this.elements.swiperInstance.destroy();
      }

      if (this.variantIdChangedUnsubscriber) {
        this.variantIdChangedUnsubscriber();
      }
      if (this.mediaGalleryStateUnsubscriber) {
        this.mediaGalleryStateUnsubscriber();
      }

      this.stopVideos();

      this.classList.remove('product-media--ready');
      window.removeEventListener('resize', this.handleSwiperResize);

      if (this.elements.pauseButton) {
        this.elements.pauseButton.removeEventListener('click', this.handlePauseClick);
      }
    }
  }

  customElements.define('product-media', ProductMedia);
}